import { io } from "socket.io-client";
import { getApiBaseUrl } from "./api";
import { getStoredAuthToken } from "../utils/authStorage";

let socket = null;
let windowBridgeRegistered = false;

// Single, central bridge that fans socket events out to window CustomEvents.
// It lives here (not in a layout) so it is re-registered automatically whenever
// a socket instance is created or reconnected — otherwise a replaced socket can
// silently drop all realtime invalidations until the page is reloaded.
const registerWindowBridge = (s) => {
  if (!s || windowBridgeRegistered || typeof window === "undefined") return;
  windowBridgeRegistered = true;

  const dispatch = (name, detail) => {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  };

  s.on("invalidate:tasks", (payload) => {
    const teamId = payload?.teamId ?? payload?.detail?.teamId ?? null;
    dispatch("invalidate:tasks", { teamId, payload });
  });

  s.on("invalidate:comments", (payload) => {
    const taskId = payload?.taskId ?? payload?.detail?.taskId;
    if (taskId) {
      dispatch("invalidate:comments", { taskId });
    }
  });

  s.on("comment:created", (payload) => {
    const taskId = payload?.taskId ?? payload?.detail?.taskId;
    const comment = payload?.comment ?? payload?.detail?.comment;
    if (!taskId || !comment) return;
    dispatch("comment:created", { taskId, comment });
  });

  s.on("comment:deleted", (payload) => {
    const taskId = payload?.taskId ?? payload?.detail?.taskId;
    const commentId = payload?.commentId ?? payload?.detail?.commentId;
    if (!taskId || !commentId) return;
    dispatch("comment:deleted", { taskId, commentId });
  });

  s.on("invalidate:teams", (payload) => {
    const teamId = payload?.teamId ?? payload?.detail?.teamId ?? null;
    dispatch("invalidate:teams", { teamId, payload });
  });

  s.on("invalidate:extensions", (payload) => {
    const teamId = payload?.teamId ?? payload?.detail?.teamId;
    if (teamId) {
      dispatch("invalidate:extensions", { teamId });
    }
  });

  s.on("calendar:changed", (payload) => {
    dispatch("invalidate:calendar", payload || {});
  });

  s.on("mail:changed", (payload) => {
    dispatch("mail:changed", payload || {});
  });

  s.on("docs:changed", (payload) => {
    dispatch("invalidate:docs", payload || {});
  });
};

const createSocket = () => {
  const token = getStoredAuthToken();
  if (!token) return null;

  const url = getApiBaseUrl();
  const s = io(url, {
    autoConnect: false,
    transports: ["websocket", "polling"],
    auth: { token },
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 8000,
    timeout: 20000,
  });

  s.on("connect", () => {
    console.log("Socket connected:", s.id);
  });

  s.on("disconnect", (reason) => {
    console.warn("Socket disconnected:", reason);
  });

  s.on("connect_error", (err) => {
    console.error("Socket error:", err.message);
    if (err.message?.toLowerCase().includes("auth") || err.message === "Authentication failed") {
      console.warn("Socket auth failure — will retry with fresh token");
    }
  });

  registerWindowBridge(s);

  return s;
};

export const initSocket = () => {
  if (socket) return socket;
  socket = createSocket();
  return socket;
};

export const reconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  windowBridgeRegistered = false;
  socket = createSocket();
  return socket;
};

export const connectSocket = () => {
  if (!socket || socket.connected) return;
  socket.connect();
};

export const disconnectSocket = () => {
  if (!socket) return;
  socket.removeAllListeners();
  socket.disconnect();
  socket = null;
  windowBridgeRegistered = false;
};

export const getSocket = () => socket;

export const joinTeamRoom = (teamId) => {
  if (socket && socket.connected && teamId) {
    socket.emit("joinTeam", teamId);
  }
};

export const leaveTeamRoom = (teamId) => {
  if (socket && socket.connected && teamId) {
    socket.emit("leaveTeam", teamId);
  }
};

export const joinDocRoom = (docId, callback) => {
  if (!socket || !socket.connected || !docId) {
    if (typeof callback === "function") callback({ ok: false, message: "Socket is not connected" });
    return false;
  }
  socket.emit("docs:join", { docId }, (result) => {
    if (typeof callback === "function") callback(result || { ok: false, message: "No join acknowledgement" });
  });
  return true;
};

export const leaveDocRoom = (docId) => {
  if (socket && socket.connected && docId) socket.emit("docs:leave", { docId });
};

export const syncDocChanges = (docId, kind, data, callback) => {
  if (!socket || !socket.connected || !docId || !data) {
    if (typeof callback === "function") callback({ ok: false, message: "Socket is not connected" });
    return false;
  }
  socket.emit("docs:sync", { docId, kind, data }, (result) => {
    if (typeof callback === "function") callback(result || { ok: false, message: "No sync acknowledgement" });
  });
  return true;
};

export const sendOffer = (to, offer) => {
  if (socket && socket.connected) {
    socket.emit("conference:offer", { to, offer });
  }
};

export const sendAnswer = (to, answer) => {
  if (socket && socket.connected) {
    socket.emit("conference:answer", { to, answer });
  }
};

export const sendIceCandidate = (to, candidate) => {
  if (socket && socket.connected) {
    socket.emit("conference:ice-candidate", { to, candidate });
  }
};
